import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Product from "./Card";
import { useCart } from "../../context/CartContext";
import productAPI from "../../api/productApi";
import Breadcrump from "../../components/Breadcrump";
import SEO from "../../components/SEO";
import KeyFeatures from "./detailfeatures/KeyFeatures";
import ProductCard from "./Card";
import { Col, Row } from "react-bootstrap";
import { Heart } from "lucide-react";
import Auth from "../../utils/AuthUtils";
import wishlistAPI from "../../api/wishlistApi";
import { showError } from "../../utils/toastUtils";

function DetailPage() {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const params = new URLSearchParams(window.location.search);

  const thumbRef = useRef();

  const [product, setProduct] = useState(null);
  const [detail, setDetail] = useState(null);
  const [variants, setVariants] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [displayedImages, setDisplayedImages] = useState([]);

  const [mainImage, setMainImage] = useState(null);
  const [quantity, setQuantity] = useState(1);
 const [isWishlisted, setIsWishlisted] = useState(false);


  const [showScrollIcon, setShowScrollIcon] = useState(false);

  const [viewFixVideo, setViewFixVideo] = useState(false);

  const [title, setTitle] = useState();
  const [brand, setBrand] = useState({});
  const [skuCode, setSkuCode] = useState("");
  const [category, setCategory] = useState({});
  const [description, setDescription] = useState("");
  const [stock, setStock] = useState("");

  /******************* FETCH PRODUCT *******************/
  const fetchProductDetail = async () => {
    try {
      const res = await productAPI.details(slug);

      if (res.code === 200) {
        const {
          product,
          detail,
          brand,
          category,
          variants,
          relatedProducts,
          isWishlisted,
        } = res.data;

        setProduct(product);

        setIsWishlisted(isWishlisted || false);

        setDetail(detail);
        setBrand(brand);
        setCategory(category);
        setVariants(variants || []);
        setRelatedProducts(relatedProducts || []);

        setTitle(product?.title);
        setSkuCode(product?.skuCode);
        setDescription(detail?.description);

        // default variant
        if (variants?.length > 0) {
          setSelectedVariant(variants[0]);
        }

        // images
        const images = [];
        if (product?.thumbnail) {
          images.push(product.thumbnail);
        }

        if (detail?.images?.length) {
          images.push(...detail.images);
        }

        if (variants?.length) {
          variants.forEach((v) => {
            if (v.images?.length) {
              images.push(...v.images);
            }
          });
        }

        if (images.length > 0) {
          setMainImage(images[0]);
          setDisplayedImages(images);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProductDetail();
  }, [slug]);

  /******************* IMAGES *******************/
  const mergedImages = [
    ...(product?.thumbnail ? [product.thumbnail] : []),
    ...(detail?.images?.map((i) => i) || []),
    ...variants.flatMap((v) => v.images || []),
  ];

  /******************* VARIANT ATTRIBUTES *******************/
  const attributeMap = {};

  variants.forEach((variant) => {
    Object.entries(variant.attributes || {}).forEach(([key, value]) => {
      if (!attributeMap[key]) {
        attributeMap[key] = new Set();
      }

      attributeMap[key].add(value);
    });
  });

  const handleVariantClick = (key, value) => {
    const updated = {
      ...selectedAttributes,
      [key]: value,
    };

    setSelectedAttributes(updated);

    const matched = variants.find((v) =>
      Object.entries(updated).every(([k, val]) => v.attributes?.[k] === val),
    );

    if (matched) {
      setSelectedVariant(matched);

      if (matched.images?.length) {
        const variantImgs = matched.images;

        setDisplayedImages(variantImgs);
        setMainImage(variantImgs[0]);
      }
    }
  };

  // Create attribute map
  if (variants) {
    variants.forEach((variant) => {
      if (variant.attributes && typeof variant.attributes === "object") {
        Object.entries(variant.attributes).forEach(([key, value]) => {
          if (!attributeMap[key]) {
            attributeMap[key] = new Set();
          }
          attributeMap[key].add(value);
        });
      }
    });
  }

  // Set default attributes on mount
  useEffect(() => {
    const defaultSelected = {};
    Object.entries(attributeMap).forEach(([key, valueSet]) => {
      const values = Array.from(valueSet);
      if (values.length > 0) {
        defaultSelected[key] = values[0];
      }
    });

    setSelectedAttributes(defaultSelected);
  }, []);

  const extractYouTubeID = (url) => {
    const regExp = /(?:youtube\.com.*(?:\?|&)v=|youtu\.be\/)([^&?/]+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  // to fix bottom
  useEffect(() => {
    const handleScroll = () => {
      const quantityEl = document.querySelector(".action-btn-detail");
      const stickyEl = document.querySelector(".tf-sticky-btn-atc");
      const floatingVideo = document.querySelector(".floating-animate");

      if (!quantityEl || !stickyEl || !floatingVideo) return;

      const quantityBottom = quantityEl.getBoundingClientRect().bottom;
      const windowHeight = window.innerHeight;

      if (quantityBottom <= windowHeight) {
        stickyEl.classList.remove("show");
        floatingVideo.classList.add("active");
      } else {
        stickyEl.classList.add("show");
        floatingVideo.classList.remove("active");
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const checkScroll = () => {
      const el = thumbRef.current;
      if (el && el.scrollHeight > el.clientHeight) {
        setShowScrollIcon(true);
      } else {
        setShowScrollIcon(false);
      }
    };

    checkScroll();
    window.addEventListener("resize", checkScroll);

    return () => window.removeEventListener("resize", checkScroll);
  }, [mergedImages]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preSelectedAttributes = {};

    for (const [key, value] of params.entries()) {
      preSelectedAttributes[key] = value;
    }

    if (Object.keys(preSelectedAttributes).length > 0 && variants.length > 0) {
      setSelectedAttributes(preSelectedAttributes);

      const matched = variants.find((variant) =>
        Object.entries(preSelectedAttributes).every(
          ([key, val]) => variant.attributes?.[key] === val,
        ),
      );

      if (matched) {
        setSelectedVariant(matched);
        setTitle(`${product?.title} - ${matched.variant_name}`);
        setSkuCode(matched.sku || product?.skuCode);
        setDescription(matched.description || detail?.description);

        const variantDetailImages = matched.images
          ? matched.images.map((img) => img)
          : [];

        const mergedImages = [...variantDetailImages];

        if (mergedImages.length > 0) {
          setMainImage(mergedImages[0]);
        }
      }
    }
  }, [variants]);

  /******************* CART *******************/
  const handleAddToCart = (e) => {
    const action = e.currentTarget.dataset.action;

    try {
      addToCart(product._id, quantity, selectedVariant?._id);

      if (action === "buy-now") {
        navigate("/checkout");
      }
    } catch (err) {
      console.error("Add to cart error:", err);
    }
  };

  /******************* THUMB SCROLL *******************/

  useEffect(() => {
    const checkScroll = () => {
      const el = thumbRef.current;

      if (!el) return;

      if (el.scrollHeight > el.clientHeight) {
        setShowScrollIcon(true);
      } else {
        setShowScrollIcon(false);
      }
    };

    checkScroll();

    window.addEventListener("resize", checkScroll);

    return () => window.removeEventListener("resize", checkScroll);
  }, [mergedImages]);

  /******************* LOADING *******************/
  if (!product) return <div className="container mt-5">Loading...</div>;

  /************* FUNCTIONS FOR DEFINE*****************/
  const price = selectedVariant
    ? selectedVariant.price
    : product?.productType === "Variant"
      ? detail?.pricing?.minPrice
      : detail?.pricing?.minPrice;

  const inStock = selectedVariant ? Number(selectedVariant.stock) > 0 : true;

  const handleWishlistToggle = async (e) => {
    e.preventDefault();

    const isLoggedIn = Auth.getToken();

    if (!isLoggedIn) {
      showError("Please log in to manage your wishlist");
      return;
    }

    try {
      const res = await wishlistAPI.addProduct({ productId: product?._id });
      if (res.code === 200) {
        setIsWishlisted((prev) => !prev);
      }
    } catch (error) {
      showError("Failed to update wishlist");
    }
  };

  return (
    <>
      <Breadcrump title={product?.title} />
      <SEO meta={detail?.meta} />

      <section className="detail-page py-5">
  <div className="tf-main-product section-image-zoom">
    <div className="container">
      <div className="row gx-lg-5 align-items-start">
        
        {/* LEFT PART: Images (Col-5) */}
        <div className="col-md-5">
          <div className="custom-left-sticky">
            <div className="editorial-gallery">
              <Row className="g-3">
                {displayedImages.slice(0, 4).map((img, index) => (
                  <Col xs={6} key={index} className="mb-3">
                    <div className="secondary-img-wrapper shadow-sm rounded overflow-hidden">
                      <img
                        src={img}
                        alt={`Detail-${index}`}
                        className="img-fluid w-100 h-100 object-fit-cover"
                        style={{ cursor: 'pointer', transition: '0.3s' }}
                        onClick={() => setMainImage(img)}
                      />
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </div>
        </div>

        {/* RIGHT PART: Description (Col-7) */}
        <div className="col-md-7">
          <div className="ith-product-detail-box p-lg-4">
            
            {/* Title Section */}
            {brand && (
              <div className="mb-2">
                <h2 className="ith-product-title fw-bold mb-1" style={{ fontSize: '2rem', letterSpacing: '-0.5px' }}>
                  {title}
                </h2>
                <span className="text-muted text-uppercase small tracking-wider">{brand?.name}</span>
              </div>
            )}

            {/* Rating */}
            <div className="ith-star-rating fs-5 mb-4 text-warning">
              <i className="bi bi-star-fill"></i>
              <i className="bi bi-star-fill ms-1"></i>
              <i className="bi bi-star-fill ms-1"></i>
              <i className="bi bi-star-fill ms-1"></i>
              <i className="bi bi-star-half ms-1"></i>
              <span className="text-muted small ms-2">(4.5/5)</span>
            </div>

            {/* Availability & SKU Card */}
            <div className="ith-availability-box bg-light border-0 mb-4 p-4 rounded-4 shadow-sm">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="mb-0">
                  <strong className="text-dark">Status:</strong> 
                  <span className="ms-2 badge bg-success-subtle text-success border border-success-subtle px-3 py-2">In Stock</span>
                </p>
                
                {product?.skuCode && (
                  <span className="text-muted small">
                    <strong>SKU:</strong> {product.skuCode}
                  </span>
                )}
              </div>

              {/* Short Description */}
              {detail?.shortDescription && (
                <div className="mt-3 pt-3 border-top border-2 border-white">
                  <h6 className="fw-bold text-dark mb-2">Description</h6>
                  <p className="description-text text-secondary lh-lg mb-0">
                    {detail.shortDescription}
                  </p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="d-flex  gap-3 w-100" >
              <button onClick={() => navigate(-1)} className="btn btn-dark px-4 py-2 rounded-pill shadow-sm">
                ← Go Back
              </button>
            

              
              
            </div>
            

          </div>
        </div>

      </div>
    </div>
  </div>
</section>











      <section className="pf-wrap py-5" style={{background: "white"}}>
        <div className="container">
          <h2 className="custom-feature-heading">Product highlights</h2>

          {detail?.keyFeatures?.length > 0 && (
            <div className="pf-card">
              {detail.keyFeatures.map((feature, index) => (
                <div className="pf-row" key={index}>
                  <span className="pf-label">{feature?.label}</span>
                  <span className="pf-value">{feature?.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* <section className="custom-features-section">
        <div className="container">
          <h2 className="custom-features-heading">Product Highlights</h2>

          {detail?.keyFeatures?.length > 0 && (
            <div className="custom-features-list">
              {detail.keyFeatures.map((feature, index) => (
                <div className="custom-feature-item" key={index}>
                  <span className="custom-feature-label">{feature?.label}</span>

                  <span className="custom-feature-value">{feature?.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section> */}
      <section className="custom-desc-section " style={{background:"white"}}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="custom-desc-heading">Product Description</h2>
              <div
                className="custom-desc-content"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>
          </div>
        </div>
      </section>

      {relatedProducts && relatedProducts.length > 0 && (
        <div className="related-products-section bg-light py-5">
          <div className="container product">
            <div className="flat-animate-tab flat-controltab-nav">
              <div className="text-start mb-4">
                <h4 className="section-title">Related Products</h4>
              </div>
              <div className="row">
                {relatedProducts.map((product, index) => (
                  <div
                    className="col-lg-3 col-sm-6 col-xs-6 mb-2"
                    key={product?._id || index}
                  >
                    <Product product={product} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}










        <div className="container-fluid  p-4 rounded ith-packaging-shipping-section">
        <h4 className="mb-4 fw-bold text-dark text-start">Packing and Shipping</h4>
        <div className="row justify-content-center align-items-center">
          <div className="col-md-6">
            <div className="ith-shipping-info-box p-4 rounded shadow-sm">
              <div className="mb-3"><p className="ith-label">Packaging Regulation:</p><p className="ith-value">Wooden Box</p></div>
              <div className="mb-3"><p className="ith-label">Delivery Time:</p><p className="ith-value">7–10 days</p></div>
              <div className="mb-3"><p className="ith-label">Global Shipping:</p><p className="ith-value">DHL, FedEx, or freight</p></div>
              <div className="mb-0"><p className="ith-label">Tracking:</p><p className="ith-value">Provided after dispatch</p></div>
            </div>
          </div>
          <div className="col-md-6 text-center">
            <img src="/assets/new-images/hero-banner/packaging.png" alt="Shipping" className="img-fluid ith-shipping-image" />
          </div>
        </div>
      </div>

      {/* --- Section 3: Fixed Content (How to Order) --- */}
      <div className="container-fluid ith-order-process-section py-5 px-3 px-md-5">
        <h2 className="fw-bold text-dark mb-4">How do you order?</h2>
        <p className="text-muted mb-5">ITH makes ordering easy. Send a mail, and our team ensures you get correct parts fast.</p>
        <div className="ith-step mb-5">
          <h5 className="fw-bold">1. IDENTIFY</h5>
          <p className="text-muted">Determine the parts you need. Photos of nameplate help.</p>
        </div>
        <div className="ith-step mb-5">
          <h5 className="fw-bold">2. ORDER</h5>
          <p className="text-muted">Our sales team identifies right parts and cost-effective solutions.</p>
        </div>
        <div className="ith-step">
          <h5 className="fw-bold">3. DELIVER</h5>
          <p className="text-muted">We'll ship to your nominated location fast.</p>
        </div>
      </div>
    </>
  );
}

export default DetailPage;
